// StaggeredReveal.tsx
import styled from '@emotion/styled';
import { motion, useReducedMotion, type Variants } from 'framer-motion';
import { Children, isValidElement, useEffect, useMemo, useState, type ReactNode } from 'react';

const Container = styled.div`
    display: contents;
`;

const ItemWrapper = styled(motion.div)<{ $fullWidth?: boolean }>`
    display: flex;
    flex: ${(p) => (p.$fullWidth ? '1' : '0 1 auto')};
    height: 100%;
    overflow: clip;
`;

const animationVariants: Record<string, Variants> = {
    fadeInUp: {
        hidden: { opacity: 0, y: 24 },
        visible: { opacity: 1, y: 0 },
    },
};

export interface StaggeredRevealProps {
    children: ReactNode;
    trigger?: boolean;
    staggerDelay?: number;
    baseDuration?: number;
    animation?: keyof typeof animationVariants;
    startDelay?: number;
    onComplete?: () => void;
    fullWidth?: boolean;
}

export default function StaggeredReveal({
    children,
    trigger = true,
    staggerDelay = 120,
    baseDuration = 420,
    animation = 'fadeInUp',
    startDelay = 0,
    onComplete,
    fullWidth = false,
}: StaggeredRevealProps) {
    const items = useMemo(() => Children.toArray(children).filter(Boolean), [children]);
    const [running, setRunning] = useState(false);
    const reducedMotion = useReducedMotion();

    useEffect(() => {
        setRunning(false);
        if (!trigger) return;
        const t = window.setTimeout(() => setRunning(true), startDelay);
        return () => clearTimeout(t);
    }, [trigger, startDelay, items.length]);

    useEffect(() => {
        if (!running || items.length === 0) return;
        const total = (items.length - 1) * staggerDelay + baseDuration;
        const t = window.setTimeout(() => onComplete?.(), total);
        return () => clearTimeout(t);
    }, [running, items.length, staggerDelay, baseDuration, onComplete]);

    const variants = animationVariants[animation] ?? animationVariants.fadeInUp;

    return (
        <Container>
            {items.map((child, i) => (
                <ItemWrapper
                    key={i}
                    $fullWidth={fullWidth}
                    initial="hidden"
                    animate={running ? 'visible' : 'hidden'}
                    variants={reducedMotion ? { hidden: { opacity: 1 }, visible: { opacity: 1 } } : variants}
                    transition={{
                        duration: baseDuration / 1000,
                        delay: (i * staggerDelay) / 1000,
                        ease: [0.4, 0, 0.2, 1],
                    }}>
                    {isValidElement(child) ? child : <div>{child}</div>}
                </ItemWrapper>
            ))}
        </Container>
    );
}
